import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, startWith, switchMap } from 'rxjs';
import { Device } from 'src/app/models/centrex/device';
import { DeviceModel } from 'src/app/models/centrex/device-model';
import { ExtensionTypes } from 'src/app/models/centrex/enums/extension-types';
import { Extension } from 'src/app/models/centrex/extension';
import { DeviceModelService } from 'src/app/services/centrex/device-model.service';
import { DeviceService } from 'src/app/services/centrex/device.service';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { TranslateDatePipe } from '../../../pipes/translate-date.pipe';
import { AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle, MatListItemMeta } from '@angular/material/list';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatCard, MatCardContent, MatCardAvatar, MatCardHeader, MatCardImage, MatCardModule } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { DeviceModelDisplayPipe } from 'src/app/pipes/device-model-display.pipe';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabsModule } from '@angular/material/tabs';
import { BlfsComponent } from './blfs/blfs.component';
import { BlfType } from 'src/app/models/centrex/blftype';
import { RegistrationStates } from 'src/app/models/centrex/enums/registration-states';

@Component({
    selector: 'app-device-form',
    templateUrl: './device-form.component.html',
    styleUrls: ['./device-form.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCardModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        MatChipListbox,
        CdkDropList,
        MatChipOption,
        CdkDrag,
        MatCheckbox,
        MatList,
        MatListItem,
        MatIcon,
        MatListItemIcon,
        MatListItemTitle,
        MatListItemMeta,
        MatIconButton,
        MatSuffix,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        TranslateDatePipe,
        DeviceModelDisplayPipe,
        MatTabsModule,
        BlfsComponent,
    ],
})
export class DeviceFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    deviceInformations: Device;
    deviceForm: UntypedFormGroup;
    isCreation: boolean;
    extensions: Extension[] = [];
    selectedExtensions: Extension[] = [];
    devicesModels: DeviceModel[] = [];
    hide = true;
    selectedDeviceModel: DeviceModel;
    filteredDevicesModels: Observable<DeviceModel[]>;
    filteredExtensions: Observable<Extension[]>;
    selectedCodecs: string[] = [];
    blfsTypes: BlfType[] = [];
    registrationStates = RegistrationStates;

    public extensionTypes = ExtensionTypes;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private deviceService: DeviceService,
        private deviceModelService: DeviceModelService,
        private extensionService: ExtensionService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private deviceModelDisplayPipe: DeviceModelDisplayPipe,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.createForm();

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/devices']);
            }
        });
    }

    ngOnInit(): void {
        let deviceAddress: string;

        // Récupère l'identifiant du périphérique
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            deviceAddress = params['id'];
        });

        this.loadDatas(deviceAddress);
    }

    public loadDatas(deviceAddress: string) {
        this.spinner.show();

        if (deviceAddress) {
            this.isCreation = false;

            forkJoin([this.deviceService.getDevice(deviceAddress), this.extensionService.getExtensions(), this.deviceModelService.getModels()])
                .pipe(
                    switchMap(([device, extensions, devicesModels]) => {
                        this.deviceInformations = device;

                        this.extensions = extensions.filter((a) => a.type !== ExtensionTypes.device || (device.extensionIds.length === 1 && a.number === deviceAddress));
                        this.devicesModels = devicesModels;

                        // Met à jour le modèle de périphérique
                        if (this.deviceInformations.manufacturer && this.deviceInformations.modelName) {
                            this.selectedDeviceModel = devicesModels.find(
                                (a) => a.manufacturer === this.deviceInformations.manufacturer && a.name === this.deviceInformations.modelName
                            );
                        }
                        this.selectedCodecs = this.deviceInformations.codecs;

                        // Met à jour la liste des extensions du périphérique
                        this.selectedExtensionIds(this.deviceInformations.extensionIds);

                        // Créé le formulaire
                        this.setForm(this.deviceInformations);

                        if (this.deviceInformations.extensionIds.length > 0) {
                            this.deviceForm.get('filterExtensions').clearValidators();
                            this.deviceForm.get('filterExtensions').updateValueAndValidity();
                        }

                        return this.deviceService.getBlfsTypes(this.deviceInformations.manufacturer);
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (blfsTypes) => {
                        this.blfsTypes = blfsTypes;
                    },
                    error: () => {
                        this.setForm(this.deviceInformations);
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.deviceInformations = new Device();
            this.deviceInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';

            forkJoin([this.extensionService.getExtensions(), this.deviceModelService.getModels()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([extensions, devicesModels]) => {
                        this.extensions = extensions.filter((a) => a.type !== ExtensionTypes.device);
                        this.devicesModels = devicesModels;

                        // Créé le formulaire
                        this.setForm(this.deviceInformations);
                    },
                    error: () => {
                        this.setForm(this.deviceInformations);
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm() {
        this.deviceForm = this.formbuilder.group({
            address: new FormControl('', Validators.pattern(/^([\da-f]{2}){6}$/i)),
            name: new FormControl(),
            manufacturer: new FormControl(),
            modelName: new FormControl(),
            organizationId: new FormControl(),
            filterExtensions: new FormControl(),
            data: new FormControl(),
            password: new FormControl(),
            rport: new FormControl(),
            vlanId: new FormControl(),
            codecs: new FormControl(),
            deviceModel: new FormControl('', [this.autocompleteDeviceModelValidator()]),
            stun: new FormControl(),
            stunServer: new FormControl(),
            stunPort: new FormControl(),
            // state: new FormControl(),
            // registrationTime: new FormControl(),
            // unregistrationTime: new FormControl(),
            // publicAddress: new FormControl(),
            // privateAddress: new FormControl(),
        });

        this.deviceForm.get('filterExtensions').setValidators([Validators.required]);
        this.deviceForm.get('filterExtensions').updateValueAndValidity();
    }

    setForm(device: Device) {
        this.filteredDevicesModels = this.deviceForm.get('deviceModel').valueChanges.pipe(
            startWith<string | DeviceModel>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : this.deviceModelDisplayPipe.transform(value);
                return name ? this.filterDevicesModels(name as string) : this.devicesModels.slice();
            })
        );

        this.filteredExtensions = this.deviceForm.get('filterExtensions').valueChanges.pipe(
            startWith<string | Extension>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : this.extensionDisplayPipe.transform(value);
                return name ? this.filterExtensions(name as string) : this.extensions.slice();
            })
        );

        this.deviceForm.controls['address'].setValue(device.address);
        this.deviceForm.controls['name'].setValue(device.name);
        this.deviceForm.controls['manufacturer'].setValue(device.manufacturer);
        this.deviceForm.controls['modelName'].setValue(device.modelName);
        this.deviceForm.controls['organizationId'].setValue(device.organizationId);
        this.deviceForm.controls['filterExtensions'].setValue('');
        this.deviceForm.controls['data'].setValue(device.data);
        this.deviceForm.controls['password'].setValue(device.password);
        this.deviceForm.controls['rport'].setValue(device.rport);
        this.deviceForm.controls['vlanId'].setValue(device.vlanId);
        this.deviceForm.controls['codecs'].setValue(device.codecs);
        this.deviceForm.controls['deviceModel'].setValue(this.selectedDeviceModel);
        this.deviceForm.controls['stun'].setValue(device.stun);
        this.deviceForm.controls['stunServer'].setValue(device.stunServer);
        this.deviceForm.controls['stunPort'].setValue(device.stunPort);

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        console.log('deviceInformations', this.deviceInformations);

        if (this.isCreation) {
            // Appel de la méthode de création
            this.deviceService
                .addDevice(this.deviceInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (deviceCreated) => {
                        this.toastr.success(this.translate.instant('Device.SuccessMessageCreateDevice'));

                        // Redirige sur la page des périphériques
                        this.router.navigate(['/devices']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Device.ErrorMessageCreateDevice'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.deviceService
                .updateDevice(this.deviceInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (device) => {
                        this.toastr.success(this.translate.instant('Device.SuccessMessageUpdateDevice'));

                        this.router.navigate(['/devices']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Device.ErrorMessageUpdateDevice'));
                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.deviceInformations.name = this.deviceForm.value.name;
        this.deviceInformations.address = this.deviceForm.value.address;
        this.deviceInformations.manufacturer = this.deviceForm.value.manufacturer;
        this.deviceInformations.modelName = this.deviceForm.value.modelName;

        let extensionsIds: string[] = [];

        // Récupère les extensions de type périphérique
        this.deviceInformations.extensionIds.forEach((extension) => {
            if (this.extensions.find((a) => a.id === extension) === undefined) {
                extensionsIds.push(extension);
            }
        });

        // Ajoute les extensions sélectionnées
        this.selectedExtensions.forEach((x) => {
            extensionsIds.push(x.id);
        });
        this.deviceInformations.extensionIds = extensionsIds;

        this.deviceInformations.data = this.deviceForm.value.data;
        this.deviceInformations.rport = this.deviceForm.value.rport;
        this.deviceInformations.vlanId = this.deviceForm.value.vlanId ? this.deviceForm.value.vlanId : 0;
        console.log('liste des codecs sélectionnés', this.deviceForm.value.codecs);
        this.deviceInformations.codecs = this.deviceForm.value.codecs.length > 1 ? this.deviceForm.value.codecs : ['PCMA', 'PCMU'];
        this.deviceInformations.password = this.deviceForm.value.password;
        this.deviceInformations.stun = this.deviceForm.value.stun;
        this.deviceInformations.stunServer = this.deviceForm.value.stunServer;
        this.deviceInformations.stunPort = this.deviceForm.value.stunPort ? this.deviceForm.value.stunPort : 3478;
        this.deviceInformations.blfs = this.deviceInformations.blfs.filter((a) => a.type !== 0).length > 0 ? this.deviceInformations.blfs : [];
        this.deviceInformations.extenders = this.deviceInformations.extenders.filter((a) => a.type !== 0).length > 0 ? this.deviceInformations.extenders : [];
    }

    selectedExtensionIds(extensionsId: string[]) {
        this.selectedExtensions = [];
        extensionsId.forEach((id) => {
            let extension = this.extensions.find((a) => a.id === id);

            if (extension) {
                extension.isSelected = true;

                this.selectedExtensions.push(extension);
            }
        });
    }

    selectedCodecsChange(codecs: string[]) {
        this.selectedCodecs = codecs;
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.selectedCodecs, event.previousIndex, event.currentIndex);
    }

    deviceModelSelectionChange(event: MatAutocompleteSelectedEvent) {
        let deviceModel = event.option.value as DeviceModel;
        if (deviceModel) {
            this.deviceForm.get('manufacturer').setValue(deviceModel.manufacturer);
            this.deviceForm.get('modelName').setValue(deviceModel.name);
            this.selectedDeviceModel = deviceModel;
        }
    }

    displayDeviceModel = (deviceModel: DeviceModel): string => {
        return deviceModel && deviceModel?.manufacturer ? this.deviceModelDisplayPipe.transform(deviceModel) : '';
    };

    displayExtension = (extension: Extension): string => {
        return extension && extension?.name ? this.extensionDisplayPipe.transform(extension) : '';
    };

    deleteExtension(extension: Extension) {
        let index = this.selectedExtensions.indexOf(extension);
        this.selectedExtensions.splice(index, 1);

        this.filteredExtensions
            .pipe(
                map((x) => {
                    let i = x.findIndex((a) => a.id === extension.id);
                    x[i].isSelected = false;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        if (this.selectedExtensions.length > 0) {
            this.deviceForm.get('filterExtensions').clearValidators();
        } else {
            this.deviceForm.get('filterExtensions').setValidators([Validators.required]);
        }
        this.deviceForm.get('filterExtensions').updateValueAndValidity();

        // this.deviceForm
        // 	.get('extensionIds')
        // 	.setValue(this.selectedExtensions.map(({ id }) => id));
    }

    optionClicked(event: Event, extension: Extension) {
        event.stopPropagation();
        this.toggleSelection(extension);
    }

    toggleSelection(extension: Extension) {
        extension.isSelected = !extension.isSelected;
        if (extension.isSelected) {
            this.selectedExtensions.push(extension);
        } else {
            const i = this.selectedExtensions.findIndex((x) => x.id === extension.id);
            this.selectedExtensions.splice(i, 1);
        }

        if (this.selectedExtensions.length > 0) {
            this.deviceForm.get('filterExtensions').clearValidators();
        } else {
            this.deviceForm.get('filterExtensions').setValidators([Validators.required]);
        }

        this.deviceForm.get('filterExtensions').updateValueAndValidity();

        // this.userControl.setValue(this.selectedUsers);
    }

    private filterDevicesModels(value: string): DeviceModel[] {
        const filterValue = value.toLowerCase();

        return this.devicesModels.filter((a) => this.deviceModelDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private filterExtensions(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.extensions.filter((a) => this.extensionDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private autocompleteDeviceModelValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (this.devicesModels.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
